// Review payload contract shared with T42 (GET reviews and reviews/:jobStageId):
// - review_mode is the effective 'ai_only' or 'ai_then_supervisor' mode, top-level on
//   each queue item and detail. A category-less stage is always 'ai_then_supervisor'.
//   stage.review_mode is accepted for older detail payloads. When neither is present,
//   evidence_flag/authenticity/audit_sample/ai_retries_exhausted imply ai_only and ai.status not_applicable
//   implies human-only. Otherwise decisions check both gate and recommendation for
//   an override reason without disabling a ready stage or guessing its mode.
// - queue_reason is top-level, null or the item's attempt's latest awaiting_supervisor transition reason
//   (null for running AI items), up to its first ':'. Values: human_only, skipped_cap, ai_kickoff_failed, ai_error,
//   ai_deadline_exceeded, ai_retries_exhausted, evidence_flag, authenticity, audit_sample. The UI also strips suffixes.
// - ai.gate is the stored pass-rule result 'pass' | 'fail' | null, in BOTH modes.
//   Detail current_attempt/previous_attempts carry ai.{gate, recommendation, blocking_ords,
//   violations}; queue items carry ai.{gate, recommendation, status}. In ai_only, gate
//   determines override requirements; ai_then_supervisor uses recommendation instead.
// - ai.violations stays in the original ai_verdict.violations order. violations[].ord
//   is its ZERO-based index, identical to blocking_ords and POST defects[].violation_ord.
//   The client anchors this index before severity sorting, never to a model field.
// - Attempt evidence[].ord is ONE-based, 1..8 across the whole attempt; stage.ord is
//   also one-based. Photo zoom labels number photos within their evidence slot.
// - ai.started_at/deadline_at are optional timestamps for the running-duration chip.
import { supabase } from './supabaseClient';
import { getActiveCarrierId } from './workforceBff';

// The existing bridge owns carrier headers, CSRF and carrier-switch cancellation.
async function call(method, path, body, { signal } = {}) {
  const carrierId = getActiveCarrierId();
  if (!carrierId) throw new Error('اپراتور را انتخاب کنید.');
  const assertCurrent = () => {
    if (signal?.aborted || carrierId !== getActiveCarrierId()) throw new DOMException('Cancelled', 'AbortError');
  };
  try {
    const { data: { session } } = await supabase.auth.getSession();
    assertCurrent();
    const response = await fetch(`/api/installation/${path}`, {
      method, signal,
      headers: { Accept: 'application/json',
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json().catch(() => ({}));
    assertCurrent();
    if (!response.ok) {
      const error = new Error(data.error || 'بازبینی دریافت نشد. دوباره تلاش کنید.');
      error.status = response.status;
      error.code = data.code;
      throw error;
    }
    return data;
  } catch (error) {
    if (error.name === 'AbortError' || error.status) throw error;
    throw new Error('ارتباط با سامانه برقرار نشد. دوباره تلاش کنید.');
  }
}

export function listReviews({ view = 'ready', stageKey = '', workFamily = '', signal } = {}) {
  const query = new URLSearchParams({ view });
  if (stageKey) query.set('stage_key', stageKey);
  if (workFamily) query.set('work_family', workFamily);
  return call('GET', `reviews?${query}`, undefined, { signal });
}
export const getReview = (id, options) => call('GET', `reviews/${encodeURIComponent(id)}`, undefined, options);
export const decideReview = (id, body, options) => call('POST', `reviews/${encodeURIComponent(id)}/decision`, body, options);
export const skipStage = (id, reason, options) => call('POST', `job-stages/${encodeURIComponent(id)}/skip`, { reason }, options);

export const listQualityAudits = (options) => call('GET', 'quality-audits', undefined, options);
export const getQualityAudit = (id, options) => call('GET', `quality-audits/${encodeURIComponent(id)}`, undefined, options);
export const decideQualityAudit = (id, body, options) => call('POST', `quality-audits/${encodeURIComponent(id)}/decision`, body, options);
export const listReviewSpecialties = (options) => call('GET', 'review-specialties', undefined, options);
export const saveReviewSpecialties = (id, families, options) => call('PUT', `review-specialties/${encodeURIComponent(id)}`, { families }, options);
export const listStageEscalations = (options) => call('GET', 'stage-escalations', undefined, options);
export const listEscalationCandidates = (id, options) => call('GET', `stage-escalations/${encodeURIComponent(id)}/candidates`, undefined, options);
export const resolveStageEscalation = (id, body, options) => call('POST', `stage-escalations/${encodeURIComponent(id)}/decision`, body, options);

export const listSupervisorVisits = (options) => call('GET', 'supervisor-visits', undefined, options);
export const completeSupervisorVisit = (id, note, options) => call('POST', `supervisor-visits/${encodeURIComponent(id)}/complete`, { note }, options);
